import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode
} from 'react';
import type {
  AuthContextType,
  User,
  Site,
  LoginCredentials,
  ChangePasswordData
} from '../types/auth';
import * as authApi from '../api/auth';
import { tokenManager } from '../api/client';
import { useInactivityLogout } from '../hooks/useInactivityLogout';

// =====================================================
// Configuración de inactividad
// Leer de variables de entorno de Vite.
// En docker-compose: VITE_SESSION_INACTIVITY_ENABLED y
//                    VITE_SESSION_INACTIVITY_MINUTES
// Si no están definidas: desactivado por defecto.
// =====================================================
const INACTIVITY_ENABLED =
  import.meta.env.VITE_SESSION_INACTIVITY_ENABLED === 'true';
const INACTIVITY_MINUTES =
  parseInt(import.meta.env.VITE_SESSION_INACTIVITY_MINUTES || '15', 10);

// Contexto
const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [activeSite, setActiveSite] = useState<Site | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // ── Verificar sesión al cargar ──────────────────
  useEffect(() => {
    const checkAuth = async () => {
      if (!authApi.hasActiveSession()) {
        setIsLoading(false);
        return;
      }

      try {
        const currentUser = await authApi.getCurrentUser();
        setUser(currentUser);
        setActiveSite(currentUser.activeSite);
      } catch (error) {
        console.error('Error checking auth:', error);
        tokenManager.clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // ── Login ───────────────────────────────────────
  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);

    try {
      const response = await authApi.login(credentials);
      setUser(response.user);
      setActiveSite(response.user.activeSite);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── Logout ──────────────────────────────────────
  const logout = useCallback(async () => {
    setIsLoading(true);

    try {
      await authApi.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setUser(null);
      setActiveSite(null);
      setIsLoading(false);
    }
  }, []);

  // ── Logout por inactividad ──────────────────────
  // Callback estable: llama a logout y opcionalmente
  // podría mostrar un mensaje, pero lo mantenemos simple.
  const handleInactivityTimeout = useCallback(() => {
    console.info('[Auth] Sesión cerrada por inactividad.');
    logout();
  }, [logout]);

  useInactivityLogout({
    enabled: INACTIVITY_ENABLED,
    timeoutMinutes: INACTIVITY_MINUTES,
    isAuthenticated,
    onTimeout: handleInactivityTimeout,
  });

  // ── Cambiar sede ────────────────────────────────
  const changeSite = useCallback(async (siteId: string) => {
    if (!user) return;

    const response = await authApi.changeSite(siteId);

    setActiveSite(response.activeSite);
    setUser(prev => prev ? {
      ...prev,
      activeSiteId: response.activeSite.id,
      activeSite: response.activeSite,
    } : null);
  }, [user]);

  // ── Cambiar contraseña ──────────────────────────
  const changePassword = useCallback(async (data: ChangePasswordData) => {
    await authApi.changePassword(data);

    setUser(prev => prev ? {
      ...prev,
      mustChangePassword: false,
    } : null);
  }, []);

  // ── Refrescar autenticación ─────────────────────
  const refreshAuth = useCallback(async () => {
    if (!authApi.hasActiveSession()) return;

    try {
      const currentUser = await authApi.getCurrentUser();
      setUser(currentUser);
      setActiveSite(currentUser.activeSite);
    } catch (error) {
      console.error('Error refreshing auth:', error);
      setUser(null);
      setActiveSite(null);
      tokenManager.clearTokens();
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    activeSite,
    login,
    logout,
    changeSite,
    changePassword,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export default AuthContext;
